import React, { useEffect, useState } from 'react';
import {
  ButtonSetWrapper,
  EmailField,
  ForgotPasswordClockFormInputGrid,
  StyledText,
  UsernameField,
} from '../components/shared/StyledComponents';
import {
  FORM_IDS,
  formDefaultValues,
  useLoginContext,
} from 'providers/LoginProvider';
import { Button, useFormInputHandler } from 'swipeclock-frontend';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useHistory, useLocation } from 'react-router-dom';
import { useFormValidation } from '../loginValidation';
import { usePostResetPasswordMutation } from 'services/login';
import PoweredByLabel from '../components/shared/PoweredByLabel';
import WhiteLabelLogo from '../components/shared/WhiteLabelLogo';
import AnimatedAlert from '../components/shared/AnimatedAlert';
import { useWhiteLabelContext } from 'providers/WhiteLabelProvider';

export default function ResetPassword() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [codeSent, setCodeSent] = useState(false);
  const history = useHistory();
  const location = useLocation();
  const [resetPassword] = usePostResetPasswordMutation();

  const {
    actions,
    meta: { pages },
  } = useLoginContext();
  const { state: whiteLabelState } = useWhiteLabelContext();

  const { errorContext, validationCallbackClockForgot } = useFormValidation();

  const {
    disabled,
    getError,
    handleBlur,
    handleChange,
    isValid,
    setDirty,
    validate,
    validationRef,
  } = useFormInputHandler({
    errorContext,
    ids: [FORM_IDS.USERNAME, FORM_IDS.EMAIL],
    validationCallback: validationCallbackClockForgot,
  });

  useEffect(() => {
    if (
      location.state?.[FORM_IDS.USERNAME] &&
      location.state?.[FORM_IDS.EMAIL]
    ) {
      validate();
    }
  }, [location.state]);

  const handleSendResetCode = async () => {
    try {
      const Id = validationRef.current[FORM_IDS.USERNAME]?.value;
      const Email = validationRef.current[FORM_IDS.EMAIL]?.value;

      // ResetPasswordPath is the url to be sent to the user via email
      const ResetPasswordPath =
        window.location.href.split('#')[0] + '#/clock/web/login/password/code';

      const body = { Id, Email, ResetPasswordPath };
      await resetPassword({ body }).unwrap();

      const message =
        'Success! Please check your email for the reset code then proceed to change your password';
      actions.setGlobalMessage({ message, severity: 'success' });
      setCodeSent(true);
    } catch (error) {
      console.error(error);
      const errorText = `We're sorry, the username/clock # or email address you entered is not recognized.`;
      actions.setGlobalMessage({ message: errorText, severity: 'error' });
    }
  };

  const handleBackToLogin = () => {
    actions.setCurrentPage(pages.START);
    actions.setGlobalMessage();
    history.replace('/clock/web/login');
  };

  const handleGoToCodeEntry = () => {
    const login = validationRef.current[FORM_IDS.USERNAME]?.value;
    const email = validationRef.current[FORM_IDS.EMAIL]?.value;
    const params = { login, email };

    actions.setCurrentPage(pages.RESETPASSCODE);
    actions.setGlobalMessage();
    history.replace({ pathname: '/clock/web/login/password/code', state: params });
  };

  const handleKeyDownCode = (event) => {
    if (event.key === 'Enter') {
      handleSendResetCode();
      setDirty((prevDirty) => ({
        ...prevDirty,
        [FORM_IDS.EMAIL]: true,
      }));
    }
  };

  return (
    <ForgotPasswordClockFormInputGrid
      isMobile={isMobile}
      noHeader={whiteLabelState?.noHeader}
    >
      <WhiteLabelLogo
        whiteLabelState={whiteLabelState}
        headerText="Reset Password"
      />
      <AnimatedAlert />
      <StyledText>
        {!codeSent
          ? 'Enter your username/clock # and email address to be emailed a password reset code.'
          : 'Check your email for the reset code. If you do not receive a code after a while, check your email address above and select the "resend code" button below.'}
      </StyledText>
      <UsernameField
        error={getError(FORM_IDS.USERNAME)}
        onBlur={handleBlur(FORM_IDS.USERNAME)}
        onChange={handleChange(FORM_IDS.USERNAME)}
        validationRef={validationRef}
        autoComplete="username"
        id={FORM_IDS.USERNAME}
        label="Username or Clock #"
        defaultValue={
          location.state?.[FORM_IDS.USERNAME] || formDefaultValues.username
        }
        placeholder="Username/Clock #"
        maxLength={50}
        required
        size="small"
      />
      <EmailField
        error={getError(FORM_IDS.EMAIL)}
        onBlur={handleBlur(FORM_IDS.EMAIL)}
        onChange={handleChange(FORM_IDS.EMAIL)}
        validationRef={validationRef}
        inputProps={{ onKeyDown: handleKeyDownCode }}
        label="Email"
        defaultValue={
          location.state?.[FORM_IDS.EMAIL] || formDefaultValues.email
        }
        placeholder="Email Address"
        size="small"
      />
      <ButtonSetWrapper direction="column" alignItems="center">
        {!codeSent && (
          <>
            <Button
              id="sc-send_reset_code_button"
              size="large"
              onClick={handleSendResetCode}
              sx={isMobile ? { width: '100%' } : { width: '50%' }}
              disabled={disabled || !isValid(pages.FORGOTPASS)}
            >
              SEND RESET CODE
            </Button>
            <Button
              id="sc-return_to_login_button"
              size="medium"
              variant="text"
              onClick={handleBackToLogin}
              sx={isMobile ? { width: '100%' } : { width: '50%' }}
            >
              RETURN TO LOGIN
            </Button>
          </>
        )}
        {codeSent && (
          <>
            <Button
              id="sc-change_password_with_code_button"
              size="large"
              onClick={handleGoToCodeEntry}
              sx={isMobile ? { width: '100%' } : { width: '50%' }}
            >
              CHANGE PASSWORD WITH CODE
            </Button>
            <Button
              id="sc-resend_code_button"
              size="medium"
              variant="text"
              onClick={handleSendResetCode}
              sx={isMobile ? { width: '100%' } : { width: '50%' }}
              disabled={disabled || !isValid(pages.FORGOTPASS)}
            >
              RESEND CODE
            </Button>
          </>
        )}
      </ButtonSetWrapper>
      <PoweredByLabel whiteLabelState={whiteLabelState} />
    </ForgotPasswordClockFormInputGrid>
  );
}
